import { Connector } from 'Webapp/utils/connector';
import {
  isArticleRoute,
  isSectionRoute,
} from 'Webapp/shared/app/redux/selectors/routing';
import { getPrimarySection } from 'Webapp/shared/app/redux/selectors/section';

export const AD_PAGE_TYPES = {
  TOPIC: 'topic',
  MAGAZINE: 'magazine',
  PROFILE: 'profile',
  ITEM: 'item',
} as const;

export type AdPageType = typeof AD_PAGE_TYPES[keyof typeof AD_PAGE_TYPES];

/**
 * Page type used for GPT page-level targeting and to decide which pages get
 * the leaderboard placement: topic / magazine / profile section pages and
 * on-site item (article) pages. Anything else (home, search, storyboards,
 * auth, ...) returns null and gets no ads.
 */
const adPageType = (state: Flipboard.State): AdPageType | null => {
  if (isArticleRoute(state)) {
    return AD_PAGE_TYPES.ITEM;
  }
  if (!isSectionRoute(state)) {
    return null;
  }
  // Route-first: the matched route is authoritative for what kind of page
  // this is. getPrimarySection is NOT — on topic pages the sections array
  // can lead with auxiliary sections (e.g. the explore-spotlight sidebar),
  // which mislabeled /topic/* pages as page_type=profile (observed live in
  // the GAM publisher console, 2026-07-30).
  const { path, params } = state.app.routing;
  if (path === '/:shortcut/:shortcutId' && params?.shortcut === 'topic') {
    return AD_PAGE_TYPES.TOPIC;
  }
  if (path === '/@:username') {
    return AD_PAGE_TYPES.PROFILE;
  }
  const section = getPrimarySection(state);
  if (!section || section.placeholder || section.isStoryboard) {
    return null;
  }
  if (section.isTopic || section.isSmartMagazine) {
    return AD_PAGE_TYPES.TOPIC;
  }
  if (section.isMagazine || path === '/@:username/:slug') {
    return AD_PAGE_TYPES.MAGAZINE;
  }
  if (section.isProfile) {
    return AD_PAGE_TYPES.PROFILE;
  }
  return null;
};

/**
 * Topic slug key-value where trivially available (topic sections carry a
 * topicTag at runtime).
 */
const adTopicSlug = (state: Flipboard.State): string | null => {
  if (!isSectionRoute(state)) {
    return null;
  }
  const section = getPrimarySection(state) as
    | (Flipboard.Section & { topicTag?: string })
    | undefined;
  return section?.topicTag || null;
};

const selectors = {
  adPageType,
  adTopicSlug,
};

export type ConnectAdTargetingProps = Flipboard.ConnectorProps<
  typeof selectors
>;

const connectAdTargeting: Connector = {
  selectors,
};

export default connectAdTargeting;
